// src/utils/dateUtils.js
// Unified date range utilities for all dashboards

/**
 * Get Amsterdam timezone date for consistent date calculations
 * This ensures all date calculations are based on Amsterdam timezone regardless of user's location
 * Returns a Date object representing midnight Amsterdam time
 */
export const getAmsterdamDate = () => {
  const now = new Date();

  // Get Amsterdam date string (YYYY-MM-DD)
  const amsterdamDateStr = now.toLocaleDateString('en-CA', { timeZone: 'Europe/Amsterdam' });
  const [year, month, day] = amsterdamDateStr.split('-').map(Number);

  // Dynamically calculate the UTC offset for Amsterdam on this specific date
  // This automatically handles DST transitions correctly
  const refDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0)); // noon UTC
  const amsterdamHour = parseInt(refDate.toLocaleString('en-GB', {
    timeZone: 'Europe/Amsterdam',
    hour: 'numeric',
    hour12: false
  }));
  const offsetHours = amsterdamHour - 12; // +1 for CET, +2 for CEST

  // Return midnight Amsterdam time expressed in UTC
  return new Date(Date.UTC(year, month - 1, day, -offsetHours, 0, 0, 0));
};

/**
 * Get Amsterdam timezone datetime for consistent time calculations
 */
export const getAmsterdamDateTime = () => {
  // Get current time parts in Amsterdam timezone
  const now = new Date();
  const options = { timeZone: 'Europe/Amsterdam', hour12: false };
  const dateStr = now.toLocaleDateString('en-CA', { timeZone: 'Europe/Amsterdam' });
  const timeStr = now.toLocaleTimeString('en-GB', options);

  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute, second] = timeStr.split(':').map(Number);

  // Return a Date object representing this Amsterdam time
  // Note: This returns a Date where getHours() etc return Amsterdam time values
  return new Date(year, month - 1, day, hour, minute, second);
};

/**
 * Get date range for a given period
 * Supports both standard periods and custom date ranges
 */
export const getDateRange = (period, customStart = null, customEnd = null) => {
  // Handle custom period - use Amsterdam timezone
  if (period === 'custom' && customStart && customEnd) {
    // Determine timezone offset based on month (DST: March-October = +02:00, otherwise +01:00)
    const startMonth = parseInt(customStart.split('-')[1]);
    const timezoneOffset = (startMonth >= 3 && startMonth <= 10) ? '+02:00' : '+01:00';
    
    return {
      start: new Date(customStart + 'T00:00:00' + timezoneOffset), // Amsterdam timezone
      end: new Date(customEnd + 'T23:59:59' + timezoneOffset)     // Amsterdam timezone
    };
  }

  const todayAmsterdam = getAmsterdamDate();
  
  switch (period) {
    case 'today':
      return {
        start: new Date(todayAmsterdam.getTime()),
        end: new Date(todayAmsterdam.getTime() + 24 * 60 * 60 * 1000)
      };
    
    case 'yesterday':
      const yesterday = new Date(todayAmsterdam.getTime() - 24 * 60 * 60 * 1000);
      return {
        start: yesterday,
        end: todayAmsterdam
      };
    
    case 'thisWeek':
      // Get current day of week in Amsterdam timezone
      const nowAmsterdam = getAmsterdamDateTime();
      const dayOfWeek = nowAmsterdam.getDay();
      const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      
      const thisWeekStart = new Date(todayAmsterdam);
      thisWeekStart.setDate(todayAmsterdam.getDate() - daysFromMonday);
      const thisWeekEnd = new Date(thisWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
      return { start: thisWeekStart, end: thisWeekEnd };

    case 'lastWeek':
      // Get current day of week in Amsterdam timezone
      const nowAmsterdamLast = getAmsterdamDateTime();
      const dayOfWeekLast = nowAmsterdamLast.getDay();
      const daysFromMondayLast = dayOfWeekLast === 0 ? 6 : dayOfWeekLast - 1;
      
      const lastWeekEnd = new Date(todayAmsterdam);
      lastWeekEnd.setDate(todayAmsterdam.getDate() - daysFromMondayLast);
      const lastWeekStart = new Date(lastWeekEnd.getTime() - 7 * 24 * 60 * 60 * 1000);
      return { start: lastWeekStart, end: lastWeekEnd };
    
    case 'thisMonth':
      const monthStart = new Date(todayAmsterdam.getFullYear(), todayAmsterdam.getMonth(), 1);
      const monthEnd = new Date(todayAmsterdam.getFullYear(), todayAmsterdam.getMonth() + 1, 1);
      return { start: monthStart, end: monthEnd };
    
    case 'lastMonth':
      const lastMonthStart = new Date(todayAmsterdam.getFullYear(), todayAmsterdam.getMonth() - 1, 1);
      const lastMonthEnd = new Date(todayAmsterdam.getFullYear(), todayAmsterdam.getMonth(), 1);
      return { start: lastMonthStart, end: lastMonthEnd };
    
    case 'last7days':
      return {
        start: new Date(todayAmsterdam.getTime() - 7 * 24 * 60 * 60 * 1000),
        end: new Date(todayAmsterdam.getTime() + 24 * 60 * 60 * 1000)
      };
    
    case 'last30days':
      return {
        start: new Date(todayAmsterdam.getTime() - 30 * 24 * 60 * 60 * 1000),
        end: new Date(todayAmsterdam.getTime() + 24 * 60 * 60 * 1000)
      };
    
    default:
      // Default to last 30 days
      return {
        start: new Date(todayAmsterdam.getTime() - 30 * 24 * 60 * 60 * 1000),
        end: new Date(todayAmsterdam.getTime() + 24 * 60 * 60 * 1000)
      };
  }
};

/**
 * Get previous period range for comparison
 * Handles all period types with proper date calculations
 */
export const getPreviousPeriodRange = (period, customStart = null, customEnd = null) => {
  // Handle custom period - use Amsterdam timezone
  if (period === 'custom' && customStart && customEnd) {
    // Determine timezone offset based on month (DST: March-October = +02:00, otherwise +01:00)
    const startMonth = parseInt(customStart.split('-')[1]);
    const timezoneOffset = (startMonth >= 3 && startMonth <= 10) ? '+02:00' : '+01:00';
    
    const start = new Date(customStart + 'T00:00:00' + timezoneOffset); // Amsterdam timezone
    const end = new Date(customEnd + 'T23:59:59' + timezoneOffset);     // Amsterdam timezone
    const diffMs = end.getTime() - start.getTime();
    return {
      start: new Date(start.getTime() - diffMs),
      end: start
    };
  }

  const todayAmsterdam = getAmsterdamDate();
  
  switch (period) {
    case 'today':
      const yesterday = new Date(todayAmsterdam.getTime() - 24 * 60 * 60 * 1000);
      return { start: yesterday, end: todayAmsterdam };
    
    case 'yesterday':
      const dayBeforeYesterday = new Date(todayAmsterdam.getTime() - 2 * 24 * 60 * 60 * 1000);
      const yesterdayStart = new Date(todayAmsterdam.getTime() - 24 * 60 * 60 * 1000);
      return { start: dayBeforeYesterday, end: yesterdayStart };
    
    case 'thisWeek':
      // Get current day of week in Amsterdam timezone
      const nowAmsterdamPrev = getAmsterdamDateTime();
      const currentDayOfWeek = nowAmsterdamPrev.getDay();
      const daysFromCurrentMonday = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
      
      const currentWeekStart = new Date(todayAmsterdam);
      currentWeekStart.setDate(todayAmsterdam.getDate() - daysFromCurrentMonday);
      
      // Calculate how many days have passed in current week (including today)
      const daysPassedInCurrentWeek = daysFromCurrentMonday + 1;
      
      // Previous week should show same number of days
      const prevWeekStart = new Date(currentWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000);
      const prevWeekEnd = new Date(prevWeekStart.getTime() + daysPassedInCurrentWeek * 24 * 60 * 60 * 1000);
      
      return { start: prevWeekStart, end: prevWeekEnd };
    
    case 'lastWeek':
      // Get current day of week in Amsterdam timezone
      const nowAmsterdamLastPrev = getAmsterdamDateTime();
      const dayOfWeekLast = nowAmsterdamLastPrev.getDay();
      const daysFromMondayLast = dayOfWeekLast === 0 ? 6 : dayOfWeekLast - 1;
      
      const lastWeekEnd = new Date(todayAmsterdam);
      lastWeekEnd.setDate(todayAmsterdam.getDate() - daysFromMondayLast);
      const lastWeekStart = new Date(lastWeekEnd.getTime() - 7 * 24 * 60 * 60 * 1000);
      const prevLastWeekStart = new Date(lastWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000);
      return { start: prevLastWeekStart, end: lastWeekStart };
    
    case 'thisMonth':
      const currentDayOfMonth = todayAmsterdam.getDate();

      // Previous month should show same number of days as current month has passed
      const lastMonthStart = new Date(todayAmsterdam.getFullYear(), todayAmsterdam.getMonth() - 1, 1);
      const lastMonthEnd = new Date(todayAmsterdam.getFullYear(), todayAmsterdam.getMonth() - 1, currentDayOfMonth);

      return { start: lastMonthStart, end: lastMonthEnd };
    
    case 'lastMonth':
      const twoMonthsAgoStart = new Date(todayAmsterdam.getFullYear(), todayAmsterdam.getMonth() - 2, 1);
      const twoMonthsAgoEnd = new Date(todayAmsterdam.getFullYear(), todayAmsterdam.getMonth() - 1, 1);
      return { start: twoMonthsAgoStart, end: twoMonthsAgoEnd };
    
    case 'last7days':
      return {
        start: new Date(todayAmsterdam.getTime() - 14 * 24 * 60 * 60 * 1000),
        end: new Date(todayAmsterdam.getTime() - 7 * 24 * 60 * 60 * 1000)
      };
    
    case 'last30days':
      return {
        start: new Date(todayAmsterdam.getTime() - 60 * 24 * 60 * 60 * 1000),
        end: new Date(todayAmsterdam.getTime() - 30 * 24 * 60 * 60 * 1000)
      };
    
    default:
      // Default to previous 30 days before last 30 days
      return {
        start: new Date(todayAmsterdam.getTime() - 60 * 24 * 60 * 60 * 1000),
        end: new Date(todayAmsterdam.getTime() - 30 * 24 * 60 * 60 * 1000)
      };
  }
};

/**
 * Get human-readable period labels
 */
export const getPeriodLabel = (period) => {
  const labels = {
    today: 'Vandaag',
    yesterday: 'Gisteren', 
    thisWeek: 'Deze Week',
    lastWeek: 'Vorige Week',
    thisMonth: 'Deze Maand',
    lastMonth: 'Vorige Maand',
    last7days: 'Laatste 7 Dagen',
    last30days: 'Laatste 30 Dagen',
    custom: 'Aangepaste Periode'
  };
  return labels[period] || period;
};

/**
 * Validate if a period is supported
 */
export const isValidPeriod = (period) => {
  const validPeriods = [
    'today', 'yesterday', 'thisWeek', 'lastWeek', 
    'thisMonth', 'lastMonth', 'last7days', 'last30days', 'custom'
  ];
  return validPeriods.includes(period);
};

/**
 * Get default period for new users/sessions
 */
export const getDefaultPeriod = () => 'today';

/**
 * Convert Amsterdam time to UTC for database queries
 * Database stores timestamps in UTC, so we need to convert Amsterdam time to UTC
 */
export const convertAmsterdamToUTC = (amsterdamDate) => {
  // Get the timezone offset for Amsterdam at the given date
  const amsTime = new Date(amsterdamDate.toLocaleString("en-US", {timeZone: "Europe/Amsterdam"}));
  const utcTime = new Date(amsterdamDate.toLocaleString("en-US", {timeZone: "UTC"}));
  
  // Calculate the offset difference in milliseconds
  const offsetMs = amsTime.getTime() - utcTime.getTime();
  
  // Apply the offset to convert Amsterdam time to UTC
  return new Date(amsterdamDate.getTime() - offsetMs);
};

/**
 * Format date range for display
 */
export const formatDateRange = (start, end) => {
  const formatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/Amsterdam'
  };

  const startFormatted = start.toLocaleDateString('nl-NL', formatOptions);
  const endFormatted = end.toLocaleDateString('nl-NL', formatOptions);

  return `${startFormatted} - ${endFormatted}`;
};

/**
 * Get the appropriate granularity for chart trends based on selected period
 * @param {string} period - The selected period key
 * @returns {'day' | 'week' | 'month'} - The granularity level
 */
export const getGranularityForPeriod = (period) => {
  switch (period) {
    case 'today':
    case 'yesterday':
      return 'day';
    case 'thisWeek':
    case 'lastWeek':
    case 'last7days':
      return 'week';
    case 'thisMonth':
    case 'lastMonth':
    case 'last30days':
    case 'custom':
    default:
      return 'month';
  }
};

/**
 * Get the number of days for each granularity level
 * @param {string} granularity - 'day' | 'week' | 'month'
 * @returns {number} - Number of days
 */
export const getTrendDaysForGranularity = (granularity) => {
  switch (granularity) {
    case 'day':
      return 14;
    case 'week':
      return 84; // 12 weeks
    case 'month':
    default:
      return 180; // 6 months
  }
};

/**
 * Get extended date range for trend charts based on granularity
 * - Day granularity: 14 days
 * - Week granularity: 12 weeks (84 days)
 * - Month granularity: 6 months (180 days)
 * @param {string} period - The selected period key
 * @returns {Object} - { start, end } dates for trend data
 */
export const getTrendDateRange = (period) => {
  const todayAmsterdam = getAmsterdamDate();
  const granularity = getGranularityForPeriod(period);
  const daysBack = getTrendDaysForGranularity(granularity);

  return {
    start: new Date(todayAmsterdam.getTime() - daysBack * 24 * 60 * 60 * 1000),
    end: new Date(todayAmsterdam.getTime() + 24 * 60 * 60 * 1000) // Include today
  };
};

/**
 * Get previous trend date range for comparison
 * Returns the period immediately before the current trend range
 * @param {string} period - The selected period key
 * @returns {Object} - { start, end } dates for previous trend data
 */
export const getPreviousTrendDateRange = (period) => {
  const todayAmsterdam = getAmsterdamDate();
  const granularity = getGranularityForPeriod(period);
  const daysBack = getTrendDaysForGranularity(granularity);

  // Previous period ends where current period starts
  const currentStart = new Date(todayAmsterdam.getTime() - daysBack * 24 * 60 * 60 * 1000);

  return {
    start: new Date(currentStart.getTime() - daysBack * 24 * 60 * 60 * 1000),
    end: currentStart
  };
};

/**
 * Get the start of a week (Monday) for a given date in Amsterdam timezone
 * @param {Date} date - The date to get the week start for
 * @returns {string} - ISO date string (YYYY-MM-DD) for Monday of that week
 */
export const getWeekStart = (date) => {
  const dateStr = date.toLocaleDateString('en-CA', { timeZone: 'Europe/Amsterdam' });
  const [year, month, day] = dateStr.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  const dayOfWeek = d.getDay();
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  d.setDate(d.getDate() - daysFromMonday);
  return d.toISOString().split('T')[0];
};

/**
 * Get the start of a month for a given date in Amsterdam timezone
 * @param {Date} date - The date to get the month start for
 * @returns {string} - ISO date string (YYYY-MM-DD) for first of that month
 */
export const getMonthStart = (date) => {
  const dateStr = date.toLocaleDateString('en-CA', { timeZone: 'Europe/Amsterdam' });
  const [year, month] = dateStr.split('-').map(Number);
  return `${year}-${String(month).padStart(2, '0')}-01`;
};

/**
 * Format a period key for display in charts
 * @param {string} periodKey - The period key (date, week start, or month start)
 * @param {'day' | 'week' | 'month'} granularity - The granularity level
 * @returns {string} - Formatted label for the chart
 */
export const formatTrendLabel = (periodKey, granularity) => {
  const date = new Date(periodKey + 'T12:00:00');

  switch (granularity) {
    case 'day':
      // "4 feb" format
      return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
    case 'week':
      // "W5" format (week number)
      const weekNumber = getWeekNumber(date);
      return `W${weekNumber}`;
    case 'month':
      // "Feb" format
      return date.toLocaleDateString('nl-NL', { month: 'short' });
    default:
      return periodKey;
  }
};

/**
 * Get ISO week number for a date
 * @param {Date} date - The date
 * @returns {number} - Week number (1-53)
 */
const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};
